import { AdminNet, RouteReturn, unwrap } from 'networking';
import { create } from 'zustand';
import { devtools } from './helpers';

type DonationTotals = RouteReturn<typeof AdminNet.donation.get>['totals'];
type Donation = RouteReturn<typeof AdminNet.donation.get>['donations'][number];
type User = RouteReturn<typeof AdminNet.admin.getUser> & {
  donationData?: RouteReturn<typeof AdminNet.donation.getByUser>;
};

interface AdminUserState {
  users: User[];
  setUsers: (users: User[]) => void;
  upsertUser: (user: User) => void;
  updateUser: (id: string, update: Partial<User>) => void;
  getUser: (id: string) => User | undefined;
  fetchUser: (id: string) => Promise<User>;
  fetchUserDonations: (id: string) => Promise<User>;

  donations: Donation[];
  donationTotals: DonationTotals | null;

  addDonations: (data: RouteReturn<typeof AdminNet.donation.get>) => void;
  fetchDonations: (campaign?: string) => Promise<void>;

  _fetching: string[];
  _fetched: { donations: boolean };

  _reset: () => void;
}

export const useAdminState = create<AdminUserState>()(
  devtools(
    (set, get) => ({
      users: [],
      setUsers: (users: User[]) => set({ users }),
      upsertUser: (user: User) => {
        const { users } = get();
        const index = users.findIndex(u => u.id === user.id);
        if (index === -1) set({ users: [...users, user] });
        else set({ users: [...users.slice(0, index), user, ...users.slice(index + 1)] });
      },
      updateUser: (id: string, update: Partial<User>) => {
        const { users } = get();
        const index = users.findIndex(u => u.id === id);
        if (index === -1) return;
        const user = users[index];
        set({ users: [...users.slice(0, index), { ...user, ...update }, ...users.slice(index + 1)] });
      },
      getUser: (id: string) => get().users.find(user => user.id === id),
      fetchUser: async (id: string) => {
        const res = await AdminNet.admin.getUser(id);
        if (res.success === false) return;

        get().upsertUser(res.data);
        return res.data;
      },
      fetchUserDonations: async (id: string) => {
        const donationData = await unwrap(AdminNet.donation.getByUser(id));
        const user = get().getUser(id);
        if (!user) return;
        user.donationData = donationData;
        get().upsertUser(user);
        return user;
      },

      donations: [],
      donationTotals: null,

      addDonations: (data: RouteReturn<typeof AdminNet.donation.get>) =>
        set(s => ({
          ...s,
          donations: [...s.donations, ...data.donations.map(d => ({ ...d, createdAt: new Date(d.createdAt) }))],
          donationTotals: data.totals,
        })),
      fetchDonations: async (campaign?: string) => {
        if (get()._fetching.includes('donations')) return;
        set(s => ({ ...s, _fetching: [...s._fetching, 'donations'] }));

        const getOldest = (campaign?: string) => {
          const donationsInCategory = get()
            .donations.filter(d => (campaign ? d.campaignId === campaign : true))
            .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
          return donationsInCategory[donationsInCategory.length - 1]?.createdAt;
        };

        get().addDonations(await unwrap(AdminNet.donation.get(getOldest(campaign)?.getTime(), campaign)));

        set(s => ({
          ...s,
          _fetching: s._fetching.filter(r => r !== 'donations'),
          _fetched: { ...s._fetched, donations: true },
        }));
      },

      _fetching: [],
      _fetched: { donations: false },

      _reset: () =>
        set({ users: [], donations: [], donationTotals: null, _fetching: [], _fetched: { donations: false } }),
    }),
    { name: 'admin' },
  ),
);
